import type { MaybeRef } from "vue"
import type { ServerResponse } from "@/http"
import type { IEventVaultResponse } from "@/types"
import { useQuery } from "@tanstack/vue-query"
import { computed, toValue } from "vue"
import { axios } from "@/axios"
import { EventVault } from "@/models"

const possibleIncludes = ["zip", "welcomevid", "website"] as const // eslint-disable-line unused-imports/no-unused-vars
type IPossibleIncludes = (typeof possibleIncludes)[number]

interface IGetEventVaultQuery {
  include?: IPossibleIncludes[]
  access?: string
}

interface IUseGetEventVaultQuery {
  include?: IPossibleIncludes[]
  access?: MaybeRef<string | undefined>
}

interface IGetEventVaultResponseMeta {
  MaxPhotos?: number
  PhotoCount?: number
}

function appendQuery(endpoint: string, query: URLSearchParams) {
  if (!query.size)
    return endpoint

  return `${endpoint}?${query.toString()}`
}

export async function getEventVault(eventVaultId?: number, { include, access }: IGetEventVaultQuery = {}) {
  if (!eventVaultId) {
    throw new Error(
      "Missing `eventVaultId` when attempting to get event vault. Please ensure `eventVaultId` is provided.",
    )
  }

  const endpoint = `api/event/${eventVaultId}`

  const query = new URLSearchParams()

  if (include) {
    query.append("include", include.toString())
  }

  if (access) {
    query.append("access", access)
  }

  const response = await axios.get<ServerResponse<IEventVaultResponse, IGetEventVaultResponseMeta>>(appendQuery(endpoint, query))

  const eventData = response.data.Data

  const meta = {
    maxPhotos: response.data.Meta?.MaxPhotos,
    photoCount: response.data.Meta?.PhotoCount,
  }

  if (eventData) {
    eventData.MaxPhotos = meta.maxPhotos
    eventData.PhotoCount = meta.photoCount
  }

  return EventVault.fromResponse(eventData)
}

export const GET_EVENT_VAULT_QUERY_KEY = "getEventVault"

export function useGetEventVault(eventVaultId?: MaybeRef<number | undefined>, queryParams: IUseGetEventVaultQuery = {}) {
  return useQuery({
    queryKey: [GET_EVENT_VAULT_QUERY_KEY, eventVaultId, queryParams.include, queryParams.access],
    queryFn: async () => getEventVault(toValue(eventVaultId)!, {
      include: queryParams.include,
      access: toValue(queryParams.access),
    }),
    enabled: computed(() => !!toValue(eventVaultId)),
    retry: false,
  })
}
